import React from 'react'
import { Icon } from 'antd'

import ReferenceRequestCard from '../ReferenceRequestCard'
import StoryboardPlanPreview from '../StoryboardPlanPreview'
import {
  formatCount,
  formatFileSize,
  getStoryboardPlanCount,
  renderFormattedMessage
} from '../../utils'
import './MessageList.scss'

const getActionName = action =>
  String(
    action?.name ||
      action?.tool_name ||
      action?.toolName ||
      action?.tool ||
      action?.action ||
      action?.event_name ||
      action?.eventName ||
      action?.type ||
      action?.event ||
      ''
  )
    .trim()
    .toLowerCase()

const getActionInput = action => {
  if (!action || typeof action !== 'object') return {}
  if (action.input && typeof action.input === 'object') return action.input
  if (action.params && typeof action.params === 'object') return action.params
  if (action.arguments && typeof action.arguments === 'object') {
    return action.arguments
  }
  if (action.args && typeof action.args === 'object') return action.args
  return {}
}

const getActionResult = action => {
  if (!action || typeof action !== 'object') return {}
  if (action.result && typeof action.result === 'object') return action.result
  if (action.output && typeof action.output === 'object') return action.output
  if (action.data && typeof action.data === 'object') return action.data
  return action
}

const getMessageActions = message => {
  const metadataActions = Array.isArray(message?.metadata?.actions)
    ? message.metadata.actions
    : []
  if (metadataActions.length) return metadataActions
  return Array.isArray(message?.actions) ? message.actions : []
}

const getMessageUploads = message => {
  const metadataUploads = Array.isArray(message?.metadata?.uploads)
    ? message.metadata.uploads
    : []
  if (metadataUploads.length) return metadataUploads
  if (Array.isArray(message?.uploads)) return message.uploads
  if (Array.isArray(message?.files)) return message.files
  return []
}

const getDisplayMessageContent = content => {
  const value = String(content || '').trim()
  const actionMatch = value.match(/^button action:\s*([^.\n]+)\.?/i)

  if (!actionMatch) return value

  const actionLabel = actionMatch[1]
    .replace(/\bnow\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()

  if (!actionLabel) return value.replace(/^button action:\s*/i, '')

  return `${actionLabel.charAt(0).toUpperCase()}${actionLabel.slice(1)}.`
}

const getUploadFilename = upload =>
  (typeof upload === 'string' && upload.split('/').pop()) ||
  upload?.filename ||
  upload?.file_name ||
  upload?.fileName ||
  upload?.name ||
  upload?.original_filename ||
  upload?.originalFilename ||
  'Attached file'

const getUploadUrl = upload =>
  (typeof upload === 'string' ? upload : '') ||
  upload?.url ||
  upload?.image_url ||
  upload?.imageUrl ||
  upload?.thumbnail_url ||
  upload?.thumbnailUrl ||
  upload?.previewUrl ||
  upload?.preview_url ||
  ''

const getUploadMimeType = upload =>
  String(
    upload?.mime_type ||
      upload?.mimeType ||
      upload?.content_type ||
      upload?.contentType ||
      upload?.type ||
      ''
  ).toLowerCase()

const isImageUpload = upload => {
  const mimeType = getUploadMimeType(upload)
  const fileName = getUploadFilename(upload).toLowerCase()
  const url = getUploadUrl(upload).toLowerCase()

  return (
    mimeType.startsWith('image/') ||
    /\.(png|jpe?g|gif|webp|svg|avif)(\?|#|$)/.test(fileName) ||
    /\.(png|jpe?g|gif|webp|svg|avif)(\?|#|$)/.test(url)
  )
}

const getUploadStatusLabel = upload =>
  upload?.status ||
  upload?.upload_status ||
  upload?.uploadStatus ||
  (getUploadUrl(upload) ? 'Uploaded' : 'Loading')

const UploadCard = ({ upload }) => {
  const fileName = getUploadFilename(upload)
  const uploadUrl = getUploadUrl(upload)
  const isImage = isImageUpload(upload)
  const statusLabel = getUploadStatusLabel(upload)
  const sizeLabel = formatFileSize(
    upload?.size || upload?.file_size || upload?.fileSize || upload?.bytes
  )
  const meta = [sizeLabel, statusLabel].filter(Boolean).join(' - ')
  const Wrapper = uploadUrl ? 'a' : 'div'
  const wrapperProps = uploadUrl
    ? {
        href: uploadUrl,
        target: '_blank',
        rel: 'noreferrer'
      }
    : {}

  return (
    <Wrapper
      className={`creative-direction-chat__attachment-card${
        isImage ? ' creative-direction-chat__attachment-card--image' : ''
      }`}
      {...wrapperProps}
    >
      <span className="creative-direction-chat__attachment-preview">
        {isImage && uploadUrl ? (
          <img src={uploadUrl} alt={fileName} />
        ) : (
          <Icon type={isImage ? 'picture' : 'file-text'} />
        )}
      </span>
      <span className="creative-direction-chat__attachment-copy">
        <strong title={fileName}>{fileName}</strong>
        {meta ? <small>{meta}</small> : null}
      </span>
      {uploadUrl ? (
        <span className="creative-direction-chat__attachment-action">
          <Icon type="link" />
        </span>
      ) : null}
    </Wrapper>
  )
}

const MessageAttachments = ({ uploads, role }) => {
  if (!uploads.length) return null

  const visibleUploads = uploads.slice(0, 2)
  const hiddenCount = uploads.length - visibleUploads.length

  return (
    <div
      className={`creative-direction-chat__attachments creative-direction-chat__attachments--${role}`}
    >
      {visibleUploads.map((upload, index) => (
        <UploadCard
          key={
            upload?.id ||
            upload?.upload_id ||
            upload?.asset_id ||
            getUploadUrl(upload) ||
            `${getUploadFilename(upload)}-${index}`
          }
          upload={upload}
        />
      ))}
      {hiddenCount > 0 ? (
        <span className="creative-direction-chat__attachment-more">
          {`+${hiddenCount}`}
        </span>
      ) : null}
    </div>
  )
}

const getStoryboardPlan = action => {
  const input = getActionInput(action)
  const result = getActionResult(action)
  const plan =
    result.plan ||
    result.storyboard_plan ||
    result.storyboardPlan ||
    result.shot_plan ||
    result.shotPlan ||
    input.plan ||
    input.storyboard_plan ||
    input.storyboardPlan ||
    null

  if (Array.isArray(plan)) {
    return {
      approved: Boolean(result.approved),
      proposed_count: result.proposed_count || result.proposedCount || plan.length,
      rationale: result.rationale || result.summary || '',
      shots: plan
    }
  }

  if (plan && typeof plan === 'object') return plan

  const hasPlanShape =
    Array.isArray(result.shots) ||
    Array.isArray(result.panels) ||
    result.proposed_count ||
    result.proposedCount ||
    result.rationale ||
    result.summary ||
    result.narrative

  return hasPlanShape ? result : null
}

const isPlanAction = action =>
  [
    'propose_plan',
    'propose_storyboard_plan',
    'generate_storyboard_plan',
    'update_plan',
    'plan_updated',
    'storyboard_plan_updated',
    'approve_plan'
  ].includes(getActionName(action))

const isFinalReportAction = action => {
  const actionName = getActionName(action)
  const result = getActionResult(action)

  return (
    actionName.includes('finalize') ||
    actionName.includes('guideline') ||
    actionName.includes('report') ||
    Boolean(result.creative_guideline || result.creativeGuideline || result.bible)
  )
}

const buildMessageEventMap = messages => {
  let storyboardPlanState = null
  let storyboardPlanApproved = false
  const eventsByMessageId = {}

  messages.forEach(message => {
    const actions = getMessageActions(message)
    if (!actions.length) return

    const cards = []

    actions.forEach((action, actionIndex) => {
      const actionName = getActionName(action)

      if (isPlanAction(action)) {
        const plan = getStoryboardPlan(action)
        const result = getActionResult(action)
        const actionApproved =
          actionName === 'approve_plan'
            ? result.approved !== false
            : Boolean(result.approved)

        if (plan) {
          storyboardPlanState = plan
        }

        storyboardPlanApproved =
          storyboardPlanApproved ||
          Boolean(plan?.approved) ||
          actionApproved

        if (plan || (actionName === 'approve_plan' && storyboardPlanState)) {
          cards.push({
            type: 'storyboardPlan',
            key: `${message.id}-plan-${actionIndex}`,
            plan: plan || storyboardPlanState,
            approved: storyboardPlanApproved
          })
        }
      }

      if (isFinalReportAction(action)) {
        cards.push({
          type: 'finalReport',
          key: `${message.id}-report-${actionIndex}`
        })
      }
    })

    if (cards.length) {
      eventsByMessageId[message.id] = cards
    }
  })

  return eventsByMessageId
}

const FinalReportEventCard = ({ onViewFinalizedReport }) => (
  <section className="creative-direction-chat__event-card creative-direction-chat__event-card--report">
    <div className="creative-direction-chat__event-card-header">
      <div>
        <span>Creative guideline</span>
        <strong>Final report is ready</strong>
      </div>
      <Icon type="file-done" />
    </div>
    <button
      type="button"
      className="creative-direction-chat__event-toggle"
      onClick={onViewFinalizedReport}
    >
      View report
      <Icon type="file-done" />
    </button>
  </section>
)

const MessageEventCards = ({
  events,
  onViewFinalizedReport
}) => {
  if (!events || !events.length) return null

  return (
    <div className="creative-direction-chat__event-stack">
      {events.map(event => {
        if (event.type === 'storyboardPlan') {
          const planCount = getStoryboardPlanCount(event.plan)

          return (
            <section
              key={event.key}
              className="creative-direction-chat__chat-artifact creative-direction-chat__chat-artifact--storyboard-plan creative-direction-chat__chat-artifact--message-event creative-direction-chat__drawer-doc-section"
            >
              <div className="creative-direction-chat__drawer-doc-title">
                <Icon type="profile" />
                <span>Storyboard Plan</span>
                <span className="creative-direction-chat__drawer-doc-count">
                  {formatCount(planCount)}
                </span>
              </div>
              <div className="creative-direction-chat__chat-storyboard-plan-body">
                <StoryboardPlanPreview
                  plan={event.plan}
                  planApproved={event.approved}
                  previewLimit={3}
                  showMoreControl
                />
              </div>
            </section>
          )
        }

        if (event.type === 'finalReport') {
          return (
            <FinalReportEventCard
              key={event.key}
              onViewFinalizedReport={onViewFinalizedReport}
            />
          )
        }

        return null
      })}
    </div>
  )
}

const MessageList = ({
  messages,
  showTyping = false,
  activeReferenceMessageId = '',
  acceptedExtensions = [],
  phaseBusyAction = '',
  referenceUploadProgress = 0,
  referenceUploadStatus = '',
  referenceUploadMessage = '',
  onUploadRequestedReference = null,
  onViewFinalizedReport = null,
  inlineArtifactsByMessageId = {}
}) => {
  if (!messages.length && !showTyping) return null
  const eventsByMessageId = buildMessageEventMap(messages)

  return (
    <div className="creative-direction-chat__phase-messages">
      {messages.map(item => {
        const inlineArtifact = inlineArtifactsByMessageId[item.id]
        const displayContent = getDisplayMessageContent(item.content)
        const uploads = getMessageUploads(item)
        const hasReferenceRequest = Boolean(
          item.role === 'assistant' &&
            !item.streaming &&
            item.id === activeReferenceMessageId &&
            item.metadata?.referenceRequest
        )

        return (
          <React.Fragment key={item.id}>
            <div
              className={`creative-direction-chat__message creative-direction-chat__message--${item.role}`}
            >
              <div className="creative-direction-chat__avatar">
                <Icon type={item.role === 'assistant' ? 'robot' : 'user'} />
              </div>
              <div className="creative-direction-chat__bubble">
                <div className="creative-direction-chat__role">
                  {item.role === 'assistant' ? 'AI' : 'You'}
                </div>
                <div className="creative-direction-chat__content">
                  {item.role === 'assistant'
                    ? renderFormattedMessage(displayContent)
                    : displayContent}
                  {item.streaming ? (
                    <span className="creative-direction-chat__cursor" />
                  ) : null}
                </div>
              </div>
            </div>
            <MessageAttachments uploads={uploads} role={item.role} />
            {hasReferenceRequest ? (
              <ReferenceRequestCard
                phase={item.phase || 'vision'}
                referenceRequest={item.metadata?.referenceRequest}
                acceptedExtensions={acceptedExtensions}
                phaseBusyAction={phaseBusyAction}
                uploadProgress={referenceUploadProgress}
                uploadStatus={referenceUploadStatus}
                uploadMessage={referenceUploadMessage}
                onUploadReference={onUploadRequestedReference}
                isInline
              />
            ) : null}
            <MessageEventCards
              events={eventsByMessageId[item.id]}
              onViewFinalizedReport={onViewFinalizedReport}
            />
            {inlineArtifact}
          </React.Fragment>
        )
      })}

      {showTyping ? (
        <div className="creative-direction-chat__message creative-direction-chat__message--assistant">
          <div className="creative-direction-chat__avatar">
            <Icon type="robot" />
          </div>
          <div className="creative-direction-chat__bubble">
            <div className="creative-direction-chat__role">AI</div>
            <div className="creative-direction-chat__typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default MessageList
